// client/src/coms/soundboard/soundboardEngine.js
// Soundboard playback. Play events arrive as LiveKit data packets sent by the SERVER (no participant)
// on the 'vox-soundboard' topic; packets from participants are ignored, so nobody can fake one.
//
// Each event has a unique event_id (duplicates are dropped, e.g. the initiator gets the event both in the
// HTTP response and from the room), names the room it was sent to (events for any other room are dropped,
// so switching channels never plays the previous channel's sounds), and carries the server time. Freshness
// is judged on THIS device's clock: an event is fresh for ttl_ms after it arrives, or after the server sent
// it when the server clock offset is known (measured from API responses) - never by comparing the server's
// absolute expiry with a local clock that may be seconds off (that silently dropped every sound on such
// devices in Soundboard 1).
//
// Clips are fetched from the authenticated media route (the event carries a short-lived signed link for
// other people's personal clips), checked against their SHA-256 id, cached in IndexedDB and decoded once.
// Soundboard audio has its own Web Audio graph - separate from voice and never mixed into the microphone:
// clip gain -> master gain (soundboard volume / mute) -> limiter -> output device chosen for voice.
import { comsApiPost } from '../comsApi';
import { cacheClip, getCachedClip } from './soundStore';
import { sha256Hex } from './audioPrep';
import { getPreferredOutputDeviceId } from '../audio/audioDevicePrefs';
import { voiceState } from '../voip/voiceclient_parts/state';

export const SOUNDBOARD_TOPIC = 'vox-soundboard';
export const MAX_SIMULTANEOUS = 6; // hard local safety; the server's per-channel limit applies first
const PREFS_KEY = 'vox:soundboardPrefs';
const LOCAL_COOLDOWN_MS = 250; // only stops accidental double clicks; the server owns real cooldowns
const DEFAULT_TTL_MS = 8000;
const LATE_SKIP_MS = 1500; // arriving later than this: start part-way through to stay in sync with others

// ---- preferences (per device) ----

export function readPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    return {
      volume: Number.isFinite(Number(raw.volume)) ? Math.max(0, Math.min(1, Number(raw.volume))) : 0.7,
      muted: !!raw.muted,
      userVolume: raw.userVolume && typeof raw.userVolume === 'object' ? raw.userVolume : {},
      mutedUsers: raw.mutedUsers && typeof raw.mutedUsers === 'object' ? raw.mutedUsers : {},
    };
  } catch {
    return { volume: 0.7, muted: false, userVolume: {}, mutedUsers: {} };
  }
}

export function writePrefs(patch) {
  const next = { ...readPrefs(), ...patch };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {}
  applyMasterGain(next);
  emitActivity({ type: 'prefs' });
  return next;
}

// ---- server clock ----
// offset = server time - local time, from the response with the shortest round trip seen so far.
const clock = { offset: null, rtt: Infinity };

export function noteServerTime(serverTs, sentAt, receivedAt = Date.now()) {
  const ts = Number(serverTs);
  if (!Number.isFinite(ts) || !Number.isFinite(Number(sentAt))) return;
  const rtt = Math.max(0, receivedAt - Number(sentAt));
  if (rtt > 5000) return;
  // Re-measure now and then (clocks drift, networks change).
  if (rtt <= clock.rtt || clock.offset == null || Math.random() < 0.2) {
    clock.offset = ts - (Number(sentAt) + rtt / 2);
    clock.rtt = Math.min(clock.rtt, rtt);
  }
}

export function serverClockOffset() {
  return clock.offset;
}

// ---- pure decision helpers (unit tested) ----

// -> null (not ours) | { rejected } | { event }. `ctx`: { now, receivedAt, offset, room }.
export function parseSoundboardPacket(payload, participant, topic, ctx = {}) {
  if (String(topic || '') !== SOUNDBOARD_TOPIC) return null;
  // Only the server may send these (participant is undefined for server-sent data).
  if (participant) return { rejected: 'not_from_server' };
  let msg;
  try {
    msg = JSON.parse(new TextDecoder().decode(payload));
  } catch {
    return { rejected: 'bad_json' };
  }
  return parseSoundboardMessage(msg, ctx);
}

export function parseSoundboardMessage(msg, ctx = {}) {
  const now = ctx.now != null ? ctx.now : Date.now();
  if (!msg || msg.__pa !== 'vox_soundboard_v1') return { rejected: 'bad_type' };
  const type = msg.type ? String(msg.type) : 'play';
  if (ctx.room && msg.room && String(msg.room) !== String(ctx.room)) return { rejected: 'other_room' };
  if (type === 'stop') {
    return { event: { type, eventId: String(msg.event_id || ''), eventIds: Array.isArray(msg.event_ids) ? msg.event_ids.map(String).slice(0, 20) : [], all: !!msg.all, by: Number(msg.by) || 0 } };
  }
  if (type === 'policy') return { event: { type, eventId: String(msg.event_id || ''), stopAll: !!msg.stop_all } };
  if (type !== 'play') return { rejected: 'bad_type' };
  if (!/^[0-9a-f]{64}$/.test(String(msg.hash || ''))) return { rejected: 'bad_hash' };
  const ttl = Math.max(1000, Math.min(30000, Number(msg.ttl_ms) || DEFAULT_TTL_MS));
  const serverTs = Number(msg.server_ts || msg.sent_at) || 0;
  // Age on this device: with a known clock offset, from when the server sent it; otherwise 0 at arrival.
  const offset = ctx.offset;
  const age = offset != null && serverTs ? Math.max(0, now + offset - serverTs) : 0;
  if (age > ttl) return { rejected: 'expired' };
  const ext = msg.ext === 'mp3' ? 'mp3' : 'wav';
  const url = typeof msg.url === 'string' && /^\/api\/coms\/media\/[0-9a-f]{64}\.(mp3|wav)(\?t=[\w.-]+)?$/.test(msg.url) ? msg.url : `/api/coms/media/${msg.hash}.${ext}`;
  return {
    event: {
      type: 'play',
      eventId: String(msg.event_id || `${msg.hash}:${serverTs}`),
      hash: msg.hash,
      ext,
      url,
      soundId: Number(msg.sound_id) || 0,
      userId: Number(msg.user_id) || 0,
      name: String(msg.name || '').slice(0, 40),
      emoji: String(msg.emoji || '').slice(0, 16),
      source: String(msg.source || ''),
      sourceServer: String(msg.source_server || '').slice(0, 100),
      volume: Math.max(0, Math.min(1, Number(msg.volume) || (msg.volume === 0 ? 0 : 1))),
      durationMs: Number(msg.duration_ms) || 0,
      age,
      deadline: (ctx.receivedAt != null ? ctx.receivedAt : now) + Math.max(0, ttl - age),
    },
  };
}

export function effectiveGain({ prefs, userId, clipVolume, deafened = false }) {
  if (!prefs || prefs.muted || deafened) return 0;
  if (prefs.mutedUsers && prefs.mutedUsers[String(userId)]) return 0;
  const uv = prefs.userVolume && prefs.userVolume[String(userId)] != null ? Number(prefs.userVolume[String(userId)]) : 1;
  const g = (Number.isFinite(uv) ? uv : 1) * (Number(clipVolume) || 0);
  return Math.max(0, Math.min(1, g));
}

// Where to start a clip that arrived `ageMs` late (null = too late, skip it).
export function lateStartSeconds(ageMs, durationMs) {
  const age = Math.max(0, Number(ageMs) || 0);
  if (age <= LATE_SKIP_MS) return 0;
  if (durationMs && age >= durationMs - 150) return null;
  return age / 1000;
}

// ---- activity (UI indicators, diagnostics) ----

const activityListeners = new Set();
export function subscribeSoundboardActivity(fn) {
  activityListeners.add(fn);
  return () => activityListeners.delete(fn);
}
function emitActivity(a) {
  activityListeners.forEach((fn) => {
    try {
      fn(a);
    } catch {}
  });
}

// Lifecycle record per event, for troubleshooting and end-to-end checks:
// received -> loaded -> decoded -> started -> audible -> ended (or skipped / failed / blocked).
const diag = { log: [], audioState: 'idle' };
function stage(eventId, name, extra) {
  const entry = { eventId, stage: name, at: Date.now(), ...(extra || {}) };
  diag.log.push(entry);
  if (diag.log.length > 200) diag.log.splice(0, diag.log.length - 200);
  emitActivity({ type: 'stage', ...entry });
}
export function getSoundboardDiagnostics() {
  return { log: diag.log.slice(), audioState: ctx ? ctx.state : 'none', room: currentRoom, offset: clock.offset, rtt: clock.rtt === Infinity ? null : clock.rtt, playing: playing.size };
}
try {
  window.__voxSoundboard = { diagnostics: getSoundboardDiagnostics };
} catch {}

// ---- audio graph ----

let ctx = null;
let master = null;
let limiter = null;
let analyser = null;
let sinkApplied = null;
const playing = new Map(); // source node -> { eventId, userId }
const fetchesInFlight = new Map();
const decoded = new Map(); // hash -> AudioBuffer (small LRU)

function audioCtx() {
  const Ctx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!Ctx) return null;
  if (!ctx) {
    try {
      ctx = new Ctx({ latencyHint: 'interactive' });
    } catch {
      ctx = new Ctx();
    }
    master = ctx.createGain();
    limiter = ctx.createDynamicsCompressor(); // safety limiter: overlapping or hot clips never clip
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.15;
    analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    master.connect(limiter);
    limiter.connect(analyser);
    analyser.connect(ctx.destination);
    applyMasterGain(readPrefs());
    try {
      ctx.onstatechange = () => emitActivity({ type: 'audio_state', state: ctx.state });
    } catch {}
  }
  applySink();
  return ctx;
}

function applyMasterGain(prefs) {
  if (!master) return;
  const p = prefs || readPrefs();
  const v = p.muted || voiceState.manualDeafened ? 0 : Math.max(0, Math.min(1, Number(p.volume) || 0));
  try {
    master.gain.setTargetAtTime(v, ctx.currentTime, 0.02);
  } catch {
    master.gain.value = v;
  }
}

// Soundboard audio follows the output device chosen for voice (headsets!), where the browser supports it.
function applySink() {
  if (!ctx || typeof ctx.setSinkId !== 'function') return;
  let want = '';
  try {
    want = String(getPreferredOutputDeviceId() || '').trim();
  } catch {}
  const id = want && want !== 'default' ? want : '';
  if (sinkApplied === id) return;
  sinkApplied = id;
  ctx.setSinkId(id).catch(() => {
    sinkApplied = null;
  });
}

export function getAudioState() {
  if (!ctx) {
    const Ctx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    return Ctx ? 'idle' : 'unsupported';
  }
  return ctx.state;
}

// Must run inside a user gesture on browsers with strict autoplay rules (Safari, some mobile browsers).
export async function unlockAudio() {
  const c = audioCtx();
  if (!c) return 'unsupported';
  if (c.state !== 'running') {
    try {
      await c.resume();
    } catch {}
  }
  // iOS needs something to be played from the gesture once.
  try {
    const b = c.createBuffer(1, 1, c.sampleRate);
    const s = c.createBufferSource();
    s.buffer = b;
    s.connect(c.destination);
    s.start(0);
  } catch {}
  emitActivity({ type: 'audio_state', state: c.state });
  return c.state;
}

// Resume on the first interaction anywhere (the voice-join click counts), so sounds others play are heard.
let unlockBound = false;
function bindUnlockOnce() {
  if (unlockBound || typeof window === 'undefined') return;
  unlockBound = true;
  const onGesture = () => {
    if (!ctx || ctx.state !== 'running') unlockAudio().catch(() => {});
  };
  ['pointerdown', 'keydown', 'touchend'].forEach((t) => window.addEventListener(t, onGesture, { capture: true, passive: true }));
}

function mimeFor(ext) {
  return ext === 'mp3' ? 'audio/mpeg' : 'audio/wav';
}

async function loadClip(hash, url, ext) {
  const cached = await getCachedClip(hash).catch(() => null);
  if (cached) return cached;
  if (fetchesInFlight.has(hash)) return fetchesInFlight.get(hash);
  const p = (async () => {
    const res = await fetch(url || `/api/coms/media/${hash}.${ext || 'wav'}`, { credentials: 'include' });
    if (!res.ok) throw Object.assign(new Error('clip_unavailable'), { status: res.status });
    const bytes = new Uint8Array(await res.arrayBuffer());
    if ((await sha256Hex(bytes)) !== hash) throw new Error('clip_hash_mismatch');
    const blob = new Blob([bytes], { type: mimeFor(ext) });
    await cacheClip(hash, blob).catch(() => {});
    return blob;
  })();
  fetchesInFlight.set(hash, p);
  try {
    return await p;
  } finally {
    fetchesInFlight.delete(hash);
  }
}

async function decodeClip(c, hash, url, ext) {
  if (decoded.has(hash)) {
    const buf = decoded.get(hash);
    decoded.delete(hash);
    decoded.set(hash, buf);
    return buf;
  }
  const blob = await loadClip(hash, url, ext);
  const ab = await blob.arrayBuffer();
  const buf = await new Promise((resolve, reject) => {
    // Older Safari only has the callback form.
    const p = c.decodeAudioData(ab, resolve, reject);
    if (p && typeof p.then === 'function') p.then(resolve, reject);
  });
  decoded.set(hash, buf);
  if (decoded.size > 40) decoded.delete(decoded.keys().next().value);
  return buf;
}

// Warm the cache for sounds the user is likely to play or hear (panel open, favourites).
export function prefetchSounds(list, max = 12) {
  const items = (list || []).filter((s) => s && /^[0-9a-f]{64}$/.test(String(s.hash || ''))).slice(0, max);
  return Promise.all(items.map((s) => loadClip(s.hash, s.url, String(s.url || '').includes('.mp3') ? 'mp3' : 'wav').catch(() => null)));
}

function watchAudible(eventId, src) {
  if (!analyser) return;
  const data = new Float32Array(analyser.fftSize);
  let peak = 0;
  let reported = false;
  const timer = setInterval(() => {
    try {
      analyser.getFloatTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length);
      if (rms > peak) peak = rms;
      if (!reported && rms > 0.005) {
        reported = true;
        stage(eventId, 'audible', { rms: Number(rms.toFixed(4)) });
      }
    } catch {}
  }, 40);
  const prev = src.onended;
  src.onended = (e) => {
    clearInterval(timer);
    if (!reported) stage(eventId, 'silent_output', { peak });
    if (typeof prev === 'function') prev(e);
  };
}

// Plays a decoded clip through the soundboard graph. -> true when it started.
async function startClip({ eventId, hash, url, ext, gain, userId, offsetSec = 0, durationMs = 0 }) {
  const c = audioCtx();
  if (!c) {
    stage(eventId, 'failed', { reason: 'unsupported' });
    return false;
  }
  if (playing.size >= MAX_SIMULTANEOUS) {
    stage(eventId, 'skipped', { reason: 'too_many' });
    return false;
  }
  const buf = await decodeClip(c, hash, url, ext);
  stage(eventId, 'decoded', { ms: Math.round(buf.duration * 1000) });
  if (c.state !== 'running') {
    try {
      await c.resume();
    } catch {}
  }
  if (c.state !== 'running') {
    stage(eventId, 'blocked', { reason: 'autoplay' });
    emitActivity({ type: 'blocked', eventId, reason: 'autoplay' });
    return false;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(g);
  g.connect(master);
  playing.set(src, { eventId, userId, gainNode: g });
  src.onended = () => {
    playing.delete(src);
    try {
      g.disconnect();
    } catch {}
    stage(eventId, 'ended');
    emitActivity({ type: 'ended', eventId });
  };
  watchAudible(eventId, src);
  const off = Math.max(0, Math.min(buf.duration - 0.05, Number(offsetSec) || 0));
  src.start(0, off);
  stage(eventId, 'started', { offset: Number(off.toFixed(2)), state: c.state, durationMs: durationMs || Math.round(buf.duration * 1000) });
  return true;
}

function stopWhere(pred) {
  let n = 0;
  for (const [src, info] of Array.from(playing.entries())) {
    if (!pred(info)) continue;
    try {
      info.gainNode.gain.setTargetAtTime(0, ctx.currentTime, 0.015);
      src.stop(ctx.currentTime + 0.06);
    } catch {
      try {
        src.stop();
      } catch {}
    }
    playing.delete(src);
    n += 1;
  }
  return n;
}

export function stopAllSounds() {
  return stopWhere(() => true);
}

// ---- room binding ----

let currentRoom = '';
const seen = new Map(); // event_id -> time (duplicates and replays are ignored)

export function setSoundboardRoom(roomName) {
  const next = String(roomName || '');
  if (next !== currentRoom) stopAllSounds();
  currentRoom = next;
  bindUnlockOnce();
  if (next) audioCtx();
  emitActivity({ type: 'room', room: next });
}

export function leaveSoundboardRoom(roomName) {
  if (roomName && currentRoom && String(roomName) !== currentRoom) return; // a late event from an older room
  stopAllSounds();
  currentRoom = '';
  emitActivity({ type: 'room', room: '' });
}

// Keeps voice deafen in step with the soundboard (deafened = hear nothing, including sounds).
export function refreshSoundboardOutput() {
  applyMasterGain(readPrefs());
  applySink();
}

function remember(eventId) {
  const now = Date.now();
  if (seen.has(eventId)) return false;
  seen.set(eventId, now);
  if (seen.size > 500) {
    for (const [k, t] of seen) if (now - t > 120000 || seen.size > 400) seen.delete(k);
  }
  return true;
}

// Handles a play/stop/policy message (from the room, or the initiator's own HTTP response).
export async function handleSoundboardMessage(msg, { roomName = currentRoom, receivedAt = Date.now() } = {}) {
  const parsed = parseSoundboardMessage(msg, { now: Date.now(), receivedAt, offset: clock.offset, room: roomName });
  const id = msg && msg.event_id ? String(msg.event_id) : '';
  if (!parsed.event) {
    if (id) stage(id, 'rejected', { reason: parsed.rejected });
    return false;
  }
  const ev = parsed.event;
  if (roomName && currentRoom && roomName !== currentRoom) return false;
  if (ev.eventId && !remember(ev.eventId)) return false;
  if (ev.type === 'stop') {
    const n = ev.all ? stopAllSounds() : stopWhere((info) => ev.eventIds.includes(info.eventId));
    emitActivity({ type: 'stopped', all: ev.all, eventIds: ev.eventIds, by: ev.by, count: n });
    return true;
  }
  if (ev.type === 'policy') {
    if (ev.stopAll) stopAllSounds();
    emitActivity({ type: 'policy' });
    return true;
  }
  stage(ev.eventId, 'received', { age: ev.age, room: roomName });
  const prefs = readPrefs();
  const gain = effectiveGain({ prefs, userId: ev.userId, clipVolume: ev.volume, deafened: !!voiceState.manualDeafened });
  const audible = gain > 0 && Number(prefs.volume) > 0;
  emitActivity({ type: 'play', ...ev, audible });
  if (!audible) {
    stage(ev.eventId, 'skipped', { reason: 'muted' });
    return true;
  }
  try {
    const c = audioCtx();
    if (c) await decodeClip(c, ev.hash, ev.url, ev.ext);
    stage(ev.eventId, 'loaded');
    if (currentRoom && roomName !== currentRoom) return true; // switched channel while loading
    const now = Date.now();
    if (now > ev.deadline) {
      stage(ev.eventId, 'skipped', { reason: 'late' });
      return true;
    }
    const lateBy = ev.age + (now - receivedAt);
    const offsetSec = lateStartSeconds(lateBy, ev.durationMs);
    if (offsetSec == null) {
      stage(ev.eventId, 'skipped', { reason: 'late' });
      return true;
    }
    await startClip({ eventId: ev.eventId, hash: ev.hash, url: ev.url, ext: ev.ext, gain, userId: ev.userId, offsetSec, durationMs: ev.durationMs });
  } catch (e) {
    stage(ev.eventId, 'failed', { reason: String((e && e.message) || e) });
    emitActivity({ type: 'failed', eventId: ev.eventId, reason: String((e && e.message) || e) });
  }
  return true;
}

export async function handleSoundboardPacket(payload, participant, topic, roomName = currentRoom) {
  if (String(topic || '') !== SOUNDBOARD_TOPIC) return false;
  const receivedAt = Date.now();
  if (participant) return true; // only the server may send these
  let msg;
  try {
    msg = JSON.parse(new TextDecoder().decode(payload));
  } catch {
    return true;
  }
  await handleSoundboardMessage(msg, { roomName, receivedAt });
  return true;
}

// ---- local preview (only you hear it; never sent to a channel) ----

let previewSrc = null;
export async function previewSound({ hash, url, blob, volume = 1 }) {
  const c = audioCtx();
  if (!c) throw new Error('This browser cannot play audio.');
  bindUnlockOnce();
  if (c.state !== 'running') await c.resume().catch(() => {});
  stopPreview();
  let buf;
  if (blob) buf = await c.decodeAudioData(await blob.arrayBuffer());
  else buf = await decodeClip(c, hash, url, String(url || '').includes('.mp3') ? 'mp3' : 'wav');
  const src = c.createBufferSource();
  src.buffer = buf;
  const g = c.createGain();
  // Previews use the soundboard volume even when soundboards are muted (you asked to hear it).
  g.gain.value = Math.max(0, Math.min(1, Number(volume) || 0)) * Math.max(0.05, readPrefs().volume);
  src.connect(g);
  g.connect(limiter);
  previewSrc = src;
  return new Promise((resolve) => {
    src.onended = () => {
      if (previewSrc === src) previewSrc = null;
      try {
        g.disconnect();
      } catch {}
      resolve(true);
    };
    src.start();
  });
}

export function stopPreview() {
  if (!previewSrc) return;
  try {
    previewSrc.stop();
  } catch {}
  previewSrc = null;
}

// Kept for older callers: play one clip locally at a gain.
export async function playClipLocally(hash, gain, { onEnded } = {}) {
  const id = `local:${hash}:${Date.now()}`;
  const ok = await startClip({ eventId: id, hash, url: '', ext: 'wav', gain, userId: 0 });
  if (ok && typeof onEnded === 'function') subscribeSoundboardActivity((a) => a.type === 'ended' && a.eventId === id && onEnded());
  return ok;
}

// ---- triggering ----

const lastLocalPlay = new Map();

async function postPlay(body) {
  const sentAt = Date.now();
  const r = await comsApiPost('/sounds/play', body);
  noteServerTime(r && r.server_ts, sentAt);
  return r;
}

// sound: a collection sound { id (sound_id), hash, ... } or a device clip { wav: Blob, serverHash?, name, emoji, volume }.
// Device clips learn their server id on first upload (the server converts and normalises them, so its id
// differs from the local file's); onServerHash lets the caller remember it. An expired copy is re-uploaded.
export async function triggerSound(roomName, sound, { onServerHash } = {}) {
  const room = String(roomName || '');
  if (!room) throw Object.assign(new Error('Join a voice channel to use the soundboard.'), { code: 'no_room' });
  const key = sound.wav ? `local:${sound.id}` : `s:${sound.id || sound.hash}`;
  const last = lastLocalPlay.get(key) || 0;
  if (Date.now() - last < LOCAL_COOLDOWN_MS) throw Object.assign(new Error('Wait a moment before playing that again.'), { code: 'cooldown', data: { code: 'cooldown', retry_after_ms: LOCAL_COOLDOWN_MS } });
  lastLocalPlay.set(key, Date.now());
  bindUnlockOnce();
  // The click is a user gesture: make sure our own audio can start.
  unlockAudio().catch(() => {});

  async function upload() {
    const fd = new FormData();
    fd.append('file', sound.wav, 'clip.wav');
    const res = await fetch('/api/coms/sounds/cache', { method: 'POST', credentials: 'include', body: fd });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw Object.assign(new Error((data && data.error) || 'Could not upload the clip.'), { data });
    if (typeof onServerHash === 'function') onServerHash(data.hash);
    return data.hash;
  }

  const play = (body) => postPlay({ room, ...body });
  let result;
  try {
    if (sound.wav) {
      let hash = sound.serverHash || '';
      if (!hash) hash = await upload();
      try {
        result = await play({ hash, name: sound.name || '', emoji: sound.emoji || '', volume: sound.volume == null ? 1 : sound.volume });
      } catch (e) {
        if (!(e && e.data && e.data.code === 'clip_missing')) throw e;
        result = await play({ hash: await upload(), name: sound.name || '', emoji: sound.emoji || '', volume: sound.volume == null ? 1 : sound.volume });
      }
    } else {
      result = await play({ sound_id: sound.id });
    }
  } catch (e) {
    throw Object.assign(new Error((e && e.data && e.data.error) || (e && e.message) || 'Could not play the sound.'), { data: e && e.data, code: e && e.data && e.data.code });
  }
  // The initiator plays from the response straight away (the copy from the room is then a duplicate).
  if (result && result.event) handleSoundboardMessage(result.event, { roomName: room, receivedAt: Date.now() }).catch(() => {});
  return result;
}

// Moderators: stop everything in the channel; anyone: stop their own sound (eventId).
export async function stopChannelSounds(roomName, eventId) {
  const sentAt = Date.now();
  const r = await comsApiPost('/sounds/stop', eventId ? { room: roomName, event_id: eventId } : { room: roomName });
  noteServerTime(r && r.server_ts, sentAt);
  return r;
}
